import { useEffect, useState } from 'react';
import UserDataService from '../../../services/user-data.service';
import { map } from 'lodash';
import { useLocation, useParams } from 'react-router-dom';
import { ChatMsg } from '../../../types/common.types';
import {
  getLocateDate,
  getLocateTime,
} from '../../../utils/format-date-time.utils';
import { UserChatListInput, UserChatsList } from '../../../types/user.types';
import DateRangeFilter, { DateRangeType } from '../../common/DateRangeFilter';
import Button from '../../common/Button';
import { ButtonSize, ButtonVariant } from '../../../types/button.enum';
import { ToastType } from '../../../types/common.types';
import * as XLSX from 'xlsx';
import FileSaver from 'file-saver';
import { useAppContext } from '../../../utils/context/app-context';

const UserChatData = () => {
  const [chatData, setChatData] = useState<ChatMsg[]>([]);
  const [totalUserChatCount, setTotalUserChatCount] = useState(0);
  const [filteredUserChatData, setFilteredUserChatData] = useState<ChatMsg[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const { id } = useParams();
  const location = useLocation();
  const userData = location.state as { name: string };
  const limit = 10;
  const [dates, setDates] = useState<DateRangeType>({
    startDate: undefined,
    endDate: undefined,
  });
  const { setToastInfo } = useAppContext();

  const getUserChatData = async (isFetchMore?: boolean) => {
    try {
      let input: UserChatListInput = {
        limit,
        offset: chatData.length,
        userId: id ?? '',
      };
      const response = await UserDataService.getUserChatData(input);
      if (response.data.data && response.data.data.userChats) {
        if (chatData.length === 0) {
          setTotalUserChatCount(response.data.data.totalUserChats);
        }
        setChatData(
          isFetchMore
            ? [...chatData, ...response.data.data.userChats]
            : response.data.data.userChats
        );
      } else {
        console.log('Error: ', response.data.message);
      }
      setLoading(false);
    } catch (err) {
      console.log('Error: ', err);
      setLoading(false);
    }
  };

  const getFilteredUserChatData = async () => {
    try {
      setLoading(true);
      const input: UserChatListInput = {
        limit,
        offset: chatData.length,
        userId: id ?? '',
        startDate: dates.startDate,
        endDate: dates.endDate,
      };
      const response = await UserDataService.getUserChatData(input);
      if (response.data.data && response.data.data.userChats) {
        setTotalUserChatCount(response.data.data.totalUserChats);
        setFilteredUserChatData([
          ...filteredUserChatData,
          ...response.data.data.userChats,
        ]);
      } else {
        console.log('Error: ', response.data.message);
      }
      setLoading(false);
    } catch (err) {
      console.log('Error: ', err);
      setLoading(false);
    }
  };

  const handleScroll = (e: any) => {
    if (
      e.target.scrollTop + e.target.clientHeight + 50 >=
        e.target.scrollHeight &&
      !loading &&
      userChatList.length < totalUserChatCount
    ) {
      if (filteredUserChatData.length > 0) {
        getFilteredUserChatData();
      } else {
        getUserChatData(true);
      }
    }
  };

  useEffect(() => {
    getUserChatData();
  }, []);

  useEffect(() => {
    if (!dates.startDate || !dates.endDate) return;
    getFilteredUserChatData();
  }, [dates]);

  const userChatList: ChatMsg[] =
    filteredUserChatData.length > 0 ? filteredUserChatData : chatData;

  const exportData = async () => {
    try {
      let input: UserChatsList = {
        userId: id ?? '',
      };
      const response = await UserDataService.downloadUserChatData(input);
      if (response.data.data && response.data.data.userChats) {
        const modifiedData = response.data.data.userChats.map(
          (chat: ChatMsg) => {
            const createdAt = new Date(chat.createdAt);
            return {
              Prompt: chat.question,
              Answer: chat.answer,
              'Token Input': chat.inputTokens,
              'Token Output': chat.outputTokens,
              'LLM Used': chat.model,
              Date: createdAt.toLocaleDateString(),
              Time: createdAt.toLocaleTimeString(),
            };
          }
        );

        const ws = XLSX.utils.json_to_sheet(modifiedData || []);
        ws['!cols'] = [
          { wch: 30 },
          { wch: 50 },
          { wch: 15 },
          { wch: 15 },
          { wch: 20 },
          { wch: 15 },
          { wch: 15 },
        ];
        const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], {
          type: 'application/octet-stream',
        });
        FileSaver.saveAs(data, `${userData?.name}.xlsx`);
      } else {
        setToastInfo({
          isOpen: true,
          type: ToastType.ERROR,
          message: 'Could not download. Please try again later',
        });
      }
    } catch (err) {
      setToastInfo({
        isOpen: true,
        type: ToastType.ERROR,
        message: 'Something went wrong',
      });
    }
  };
  return (
    <div className="overflow-y-auto w-full p-5">
      <div className="flex justify-between items-center mb-8">
        <h3 className="font-normal text-[30px]">{userData?.name}</h3>
        <div className='flex'>
          <Button
            type="button"
            disabled={!userChatList || userChatList.length === 0}
            onClick={() => exportData()}
            variant={ButtonVariant.SECONDARY}
            size={ButtonSize.SMALL}
            classes='mr-4'
          >
            Export Data
          </Button>
          <DateRangeFilter
            setDateRange={(values) => setDates(values)}
            dateRange={dates}
          />
        </div>
      </div>
      <div
        className="overflow-x-auto p-5 rounded-2xl shadow-[0_0_8px_#cbcbcb] max-h-[calc(100%-100px)] overflow-y-auto"
        onScroll={handleScroll}
      >
        <table className="text-[14px] bg-white min-w-full">
          <thead className="">
            <tr className="text-[#616161]">
              <th className="p-3 min-w-[100px] text-left">Prompt</th>
              <th className="p-3 min-w-[100px]">Token Input</th>
              <th className="p-3 min-w-[100px]">Token Output</th>
              <th className="p-3 min-w-[100px]">LLM Used</th>
              <th className="p-3 min-w-[100px]">Date</th>
              <th className="p-3 min-w-[100px]">Time</th>
            </tr>
          </thead>
          <tbody className="text-[#333] font-regular">
            {userChatList &&
              userChatList.length > 0 &&
              map(userChatList, (chatMsg) => (
                <tr className="static rounded-lg border-b-8 border-white last:border-none">
                  <td className="px-3 py-5 first:font-semibold bg-[#fcfcfc] max-w-[400px] truncate ...">
                    {chatMsg.question}
                  </td>
                  <td className="px-3 py-5 bg-[#fcfcfc]">
                    {chatMsg.inputTokens}
                  </td>
                  <td className="px-3 py-5 bg-[#fcfcfc]">
                    {chatMsg.outputTokens}
                  </td>
                  <td className="px-3 py-5 bg-[#fcfcfc]">{chatMsg.model}</td>
                  <td className="px-3 py-5 bg-[#fcfcfc]">
                    {getLocateDate(chatMsg.createdAt)}
                  </td>
                  <td className="px-3 py-5 bg-[#fcfcfc]">
                    {getLocateTime(chatMsg.createdAt)}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserChatData;
